/* ============================================================
   CONVERSION LAYER JS — Impulse custom
   Plugs into the theme's existing AJAX cart events:
   dispatching 'ajaxProduct:added' (bubbling) makes the theme
   rebuild the cart and slide the sheet up automatically.
   ============================================================ */
(function () {
  'use strict';

  /* ------------------------------------------------------------
     COMPATIBILITY SHIMS — must run before anything else.
     NodeList.forEach is missing on older iOS Safari and Android
     WebViews. This file calls it in ~19 places, and one TypeError
     kills the whole script, which is why the cart sheet and the
     details panel vanished on some devices while working on others.
     ------------------------------------------------------------ */
  if (typeof NodeList !== 'undefined' && NodeList.prototype && !NodeList.prototype.forEach) {
    NodeList.prototype.forEach = Array.prototype.forEach;
  }
  if (typeof HTMLCollection !== 'undefined' && HTMLCollection.prototype && !HTMLCollection.prototype.forEach) {
    HTMLCollection.prototype.forEach = Array.prototype.forEach;
  }
  if (typeof Element !== 'undefined' && Element.prototype && !Element.prototype.remove) {
    Element.prototype.remove = function () {
      if (this.parentNode) this.parentNode.removeChild(this);
    };
  }
  if (typeof Element !== 'undefined' && Element.prototype && !Element.prototype.closest) {
    Element.prototype.closest = function (sel) {
      var el = this;
      while (el && el.nodeType === 1) {
        if ((el.matches || el.msMatchesSelector || el.webkitMatchesSelector).call(el, sel)) return el;
        el = el.parentElement;
      }
      return null;
    };
  }

  if (typeof window.CustomEvent !== 'function') {
    var CE = function (event, params) {
      params = params || { bubbles: false, cancelable: false, detail: null };
      var evt = document.createEvent('CustomEvent');
      evt.initCustomEvent(event, !!params.bubbles, !!params.cancelable, params.detail);
      return evt;
    };
    CE.prototype = window.Event.prototype;
    window.CustomEvent = CE;
  }

  /* Runs a step in isolation. Without this, one unsupported API on an
     older device aborted the whole init sequence — which is why the
     cart sheet, details panel and other popups all stopped opening
     together on some phones while working everywhere else. */
  function safe(label, fn) {
    try { fn(); }
    catch (e) {
      if (window.console && console.warn) console.warn('[conversion layer] ' + label + ' failed:', e);
    }
  }

  var settings = (window.theme && window.theme.settings) || {};
  var routes = (window.theme && window.theme.routes) || {};

  // Force drawer mode. If the store's saved Cart type is "page", the
  // theme's AjaxProduct redirects to /cart after every add and never
  // constructs the drawer. This runs at parse time — before the
  // theme's DOMContentLoaded init reads cartType — so the slide-up
  // sheet wins regardless of the saved setting.
  if (window.theme) {
    window.theme.settings = window.theme.settings || {};
    window.theme.settings.cartType = 'drawer';
    settings = window.theme.settings;
  }

  var THRESHOLD = (parseFloat(settings.freeShipThreshold) || 170) * 100; // cents
  var SYMBOL = settings.currencySymbol || '$';

  function addedFlashText(el) {
    var host = el && el.closest ? el.closest('[data-added-text]') : null;
    return (host && host.dataset.addedText) || 'ADDED \u2713';
  }

  /* ---------- helpers ---------- */
  function money(cents) {
    var v = (cents / 100).toFixed(2);
    if (v.slice(-3) === '.00') v = v.slice(0, -3);
    return SYMBOL + v;
  }

  // Hidden carrier form so bubbled events look exactly like the
  // theme's own product-form dispatches (target has [name="id"]).
  var carrier;
  function getCarrier(variantId) {
    if (!carrier) {
      carrier = document.createElement('form');
      carrier.hidden = true;
      carrier.innerHTML = '<input type="hidden" name="id" value="">';
      document.body.appendChild(carrier);
    }
    carrier.querySelector('[name="id"]').value = variantId;
    return carrier;
  }

  function ajaxAdd(variantId, btn) {
    if (!variantId) return Promise.reject();
    if (btn) btn.classList.add('is-adding');
    return fetch(routes.cartAdd || '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
    })
      .then(function (r) { return r.json(); })
      .then(function (product) {
        if (btn) btn.classList.remove('is-adding');
        if (product && product.status && product.status !== 200) {
          throw product;
        }
        getCarrier(variantId).dispatchEvent(
          new CustomEvent('ajaxProduct:added', {
            detail: { product: product, addToCartBtn: btn },
            bubbles: true
          })
        );
        return product;
      })
      .catch(function (err) {
        if (btn) btn.classList.remove('is-adding');
        throw err;
      });
  }

  function readVariants(scope) {
    var el = scope.querySelector('[data-qa-variants]');
    if (!el) return [];
    try { return JSON.parse(el.textContent); } catch (e) { return []; }
  }

  function optAt(v, pos) { return pos === 2 ? v.o2 : pos === 3 ? v.o3 : v.o1; }

  // ctx = the currently selected values for the NON-size options (e.g. colour).
  // Without it, a size sold out in the chosen colour but available in another
  // would wrongly read as in stock, and adding would pick the wrong colour.
  function matchesCtx(v, sizePos, ctx) {
    if (!ctx) return true;
    for (var p = 1; p <= 3; p++) {
      if (p === sizePos) continue;
      var want = ctx['o' + p];
      if (want == null) continue;
      if (optAt(v, p) !== want) return false;
    }
    return true;
  }

  function resolveVariant(variants, sizePos, sizeValue, ctx) {
    var fallback = null;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (optAt(v, sizePos || 1) !== sizeValue || !v.available) continue;
      if (matchesCtx(v, sizePos, ctx)) return v;
      if (!fallback) fallback = v;
    }
    // Only fall back to another colour when no context was supplied at all.
    return ctx ? null : fallback;
  }

  function sizeAvailable(variants, sizePos, sizeValue, ctx) {
    return !!resolveVariant(variants, sizePos, sizeValue, ctx);
  }

  function ctxFromVariant(v, sizePos) {
    if (!v) return null;
    var ctx = {};
    for (var p = 1; p <= 3; p++) {
      if (p === sizePos) continue;
      var val = p === 2 ? v.option2 : p === 3 ? v.option3 : v.option1;
      if (val != null) ctx['o' + p] = val;
    }
    return ctx;
  }

  /* ============================================================
     1. QUICK ADD ON CARDS (event delegation → survives Load More)
     ============================================================ */
  function initQuickAdd(root) {
    (root || document).querySelectorAll('.quick-add:not([data-init])').forEach(function (qa) {
      qa.setAttribute('data-init', '1');
      var variants = readVariants(qa);
      var pos = parseInt(qa.dataset.sizePos || '0', 10);
      qa.querySelectorAll('.quick-add__chip').forEach(function (chip) {
        if (pos && !sizeAvailable(variants, pos, chip.dataset.value)) {
          chip.setAttribute('disabled', 'disabled');
        }
      });
    });
  }

  document.addEventListener('click', function (e) {
    var chip = e.target.closest('.quick-add__chip');
    if (chip && !chip.disabled) {
      e.preventDefault();
      var wrap = chip.closest('.quick-add');
      wrap.querySelectorAll('.quick-add__chip').forEach(function (c) { c.classList.remove('is-active'); });
      chip.classList.add('is-active');
      return;
    }

    var atc = e.target.closest('[data-qa-atc]');
    if (atc) {
      e.preventDefault();
      var qa = atc.closest('.quick-add');
      if (qa.dataset.locked === 'true') return;

      var variants = readVariants(qa);
      var variantId = null;
      var pos = parseInt(qa.dataset.sizePos || '0', 10);

      if (!pos || qa.dataset.single === 'true') {
        // one real variant → straight add
        variantId = qa.dataset.defaultVariant;
      } else {
        var active = qa.querySelector('.quick-add__chip.is-active');
        if (!active) {
          var sizes = qa.querySelector('.quick-add__sizes');
          if (sizes) {
            sizes.classList.remove('needs-pick');
            void sizes.offsetWidth;
            sizes.classList.add('needs-pick');
          }
          return;
        }
        var v = resolveVariant(variants, pos, active.dataset.value);
        variantId = v && v.id;
      }

      ajaxAdd(variantId, atc).then(function () {
        atc.classList.add('is-added');
        var label = atc.querySelector('span') || atc;
        var orig = label.textContent;
        label.textContent = addedFlashText(atc);
        setTimeout(function () {
          atc.classList.remove('is-added');
          label.textContent = orig;
        }, 1600);
      }).catch(function () {});
    }
  });

  /* ============================================================
     2. LOCKED DROPS + COUNTDOWN
     ============================================================ */
  var lockedUnits = []; // {qa, card, deadline, els}

  var LOCK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke-width="2"><rect x="4" y="10" width="16" height="11" rx="1"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>';

  function initDrops(root) {
    (root || document).querySelectorAll('.quick-add[data-locked="true"]:not([data-drop-init])').forEach(function (qa) {
      qa.setAttribute('data-drop-init', '1');
      var card = qa.closest('.grid-product');
      if (card) {
        card.classList.add('is-locked');
        var mask = card.querySelector('.grid-product__image-mask, .product-img-mask');
        if (mask && !mask.querySelector('.drop-overlay')) {
          var ov = document.createElement('div');
          ov.className = 'drop-overlay';
          ov.innerHTML =
            '<span class="drop-overlay__lock">' + LOCK_SVG + '</span>' +
            '<span class="drop-overlay__text">' + (qa.dataset.lockText || 'Coming soon') + '</span>';
          mask.appendChild(ov);
        }
      }
      var deadline = parseInt(qa.dataset.deadline || '0', 10);
      lockedUnits.push({ qa: qa, card: card, deadline: deadline });
    });

    // PDP lock note (buy box + sticky bar disable)
    (root || document).querySelectorAll('[data-pdp-lock]:not([data-drop-init])').forEach(function (note) {
      note.setAttribute('data-drop-init', '1');
      var deadline = parseInt(note.dataset.deadline || '0', 10);
      lockPdpButtons(true);
      lockedUnits.push({ qa: note, card: null, deadline: deadline, pdp: true });
    });
  }

  function lockPdpButtons(state) {
    document.querySelectorAll('.product-section [data-add-to-cart], .sticky-atc__btn, .details-panel__atc').forEach(function (b) {
      b.disabled = state;
      if (state) b.setAttribute('data-drop-disabled', '1');
      else b.removeAttribute('data-drop-disabled');
    });
  }

  function unlockUnit(u) {
    if (u.pdp) {
      lockPdpButtons(false);
      u.qa.style.display = 'none';
      return;
    }
    u.qa.dataset.locked = 'false';
    u.qa.classList.add('is-unlocked');
    if (u.card) {
      u.card.classList.remove('is-locked');
      var ov = u.card.querySelector('.drop-overlay');
      if (ov) ov.remove();
    }
    var atc = u.qa.querySelector('[data-qa-atc]');
    if (atc) {
      atc.classList.remove('quick-add__atc--locked');
      atc.removeAttribute('disabled');
      var label = atc.querySelector('span') || atc;
      label.textContent = u.qa.dataset.unlockText || 'ADD TO CART';
    }
  }

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function tick() {
    var now = Date.now();
    for (var i = lockedUnits.length - 1; i >= 0; i--) {
      var u = lockedUnits[i];
      if (!u.deadline) continue; // locked with no timer → stays locked
      var diff = u.deadline - now;
      if (diff <= 0) {
        unlockUnit(u);
        lockedUnits.splice(i, 1);
        continue;
      }
      var cd = u.qa.querySelector('.drop-countdown');
      if (!cd) continue;
      var d = Math.floor(diff / 86400000);
      var h = Math.floor(diff / 3600000) % 24;
      var m = Math.floor(diff / 60000) % 60;
      var s = Math.floor(diff / 1000) % 60;
      var nums = cd.querySelectorAll('.drop-countdown__num');
      if (nums.length === 4) {
        nums[0].textContent = pad(d);
        nums[1].textContent = pad(h);
        nums[2].textContent = pad(m);
        nums[3].textContent = pad(s);
      }
    }
  }
  setInterval(tick, 1000);

  /* ============================================================
     3. PRODUCT DETAILS SLIDE-OVER (SEE DETAILS +)
     ============================================================ */
  function panelEls() {
    return {
      panel: document.querySelector('.details-panel'),
      backdrop: document.querySelector('.details-backdrop')
    };
  }
  function openPanel() {
    var els = panelEls();
    if (!els.panel) return;
    els.panel.classList.add('is-open');
    if (els.backdrop) els.backdrop.classList.add('is-open');
    document.documentElement.classList.add('cc-noscroll');
  }
  function closePanel() {
    var els = panelEls();
    if (!els.panel) return;
    els.panel.classList.remove('is-open');
    if (els.backdrop) els.backdrop.classList.remove('is-open');
    document.documentElement.classList.remove('cc-noscroll');
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-details-open]')) { e.preventDefault(); openPanel(); return; }
    if (e.target.closest('[data-details-close]') || e.target.classList.contains('details-backdrop')) {
      closePanel();
      return;
    }
    var pAtc = e.target.closest('.details-panel__atc');
    if (pAtc && !pAtc.disabled) {
      e.preventDefault();
      var mainSelect = document.querySelector('.product-section [data-product-select]');
      var vid = pAtc.dataset.variantId;
      if (mainSelect && mainSelect.value) vid = mainSelect.value;
      ajaxAdd(vid, pAtc).then(closePanel).catch(function () {});
    }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closePanel();
  });

  /* ============================================================
     4. STICKY TOP ATC BAR
     ============================================================ */
  function initStickyBar() {
    var bar = document.querySelector('.sticky-atc');
    if (!bar) return;
    document.body.classList.add('has-sticky-atc');

    var variants = readVariants(bar);
    var pos = parseInt(bar.dataset.sizePos || '0', 10);
    var selected = bar.dataset.defaultVariant;
    var ctx = null; // selected colour / other options

    function currentSizeValue() {
      var active = bar.querySelector('.sticky-atc__chip.is-active');
      return active ? active.dataset.value : null;
    }

    // Mark every size in/out of stock FOR THE SELECTED COLOUR.
    function refreshChips() {
      if (!pos) return;
      var activeStillOk = false;
      bar.querySelectorAll('.sticky-atc__chip').forEach(function (chip) {
        var ok = sizeAvailable(variants, pos, chip.dataset.value, ctx);
        chip.classList.toggle('is-sold-out', !ok);
        if (ok) {
          chip.removeAttribute('disabled');
          chip.removeAttribute('aria-disabled');
          chip.title = '';
        } else {
          chip.setAttribute('disabled', 'disabled');
          chip.setAttribute('aria-disabled', 'true');
          chip.title = 'Sold out';
          chip.classList.remove('is-active');
        }
        if (ok && chip.classList.contains('is-active')) activeStillOk = true;
      });

      // If the colour switch sold out the chosen size, pick the first that works.
      if (!activeStillOk) {
        var first = bar.querySelector('.sticky-atc__chip:not([disabled])');
        if (first) {
          first.classList.add('is-active');
          var v = resolveVariant(variants, pos, first.dataset.value, ctx);
          if (v) selected = v.id;
        }
      }

      var btn = bar.querySelector('.sticky-atc__btn');
      var anyLeft = !!bar.querySelector('.sticky-atc__chip:not([disabled])');
      if (btn && !btn.hasAttribute('data-drop-disabled')) {
        btn.disabled = !anyLeft;
        var lbl = btn.querySelector('span') || btn;
        if (!anyLeft) {
          if (!btn.dataset.origLabel) btn.dataset.origLabel = lbl.textContent;
          lbl.textContent = bar.dataset.soldOutText || 'Sold out';
        } else if (btn.dataset.origLabel) {
          lbl.textContent = btn.dataset.origLabel;
        }
      }
    }

    bar.querySelectorAll('.sticky-atc__chip').forEach(function (chip) {
      chip.addEventListener('click', function () {
        if (chip.disabled) return;
        bar.querySelectorAll('.sticky-atc__chip').forEach(function (c) { c.classList.remove('is-active'); });
        chip.classList.add('is-active');
        var v = resolveVariant(variants, pos, chip.dataset.value, ctx);
        if (v) selected = v.id;
      });
    });

    // Follow the buy box: changing colour re-checks every size.
    document.addEventListener('variant:change', function (e) {
      var v = e && e.detail && e.detail.variant;
      if (!v) return;
      ctx = ctxFromVariant(v, pos);
      var size = currentSizeValue();
      var match = size ? resolveVariant(variants, pos, size, ctx) : null;
      selected = (match && match.id) || v.id;
      refreshChips();
      var price = bar.querySelector('.sticky-atc__price');
      if (price && typeof v.price === 'number') price.textContent = money(v.price);
    });

    refreshChips();

    var btn = bar.querySelector('.sticky-atc__btn');
    if (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        ajaxAdd(selected, btn).catch(function () {});
      });
    }
  }

  /* ============================================================
     5. CART SHEET — AOV meter, live total, promo, upsells
     ============================================================ */
  var promoCode = '';

  function refreshCartUi() {
    var form = document.getElementById('CartDrawerForm');
    if (!form) return;
    var items = form.querySelector('.cart__items');
    var subtotal = items ? parseInt(items.dataset.cartSubtotal || '0', 10) : 0;
    var count = items ? parseInt(items.dataset.count || '0', 10) : 0;

    // meter
    var meter = form.querySelector('.ship-meter');
    if (meter) {
      var fill = meter.querySelector('.ship-meter__fill');
      var label = meter.querySelector('[data-ship-label]');
      var pct = THRESHOLD > 0 ? Math.min(100, Math.round((subtotal / THRESHOLD) * 100)) : 100;
      if (subtotal >= THRESHOLD && count > 0) {
        meter.classList.add('is-achieved');
        if (label) label.textContent = meter.dataset.achievedText || 'Free shipping achieved!';
      } else {
        meter.classList.remove('is-achieved');
        var remaining = money(Math.max(0, THRESHOLD - subtotal));
        if (label) {
          label.textContent = (meter.dataset.progressText || "You're [amount] away from free shipping").replace('[amount]', remaining);
        }
      }
      if (fill) fill.style.width = pct + '%';
    }

    // checkout button live total
    var total = form.querySelector('[data-checkout-total]');
    if (total) total.textContent = money(subtotal);

    // upsell strip: auto-populate + hide items already in the cart
    upsellsSync();
  }

  function watchCartRebuilds() {
    var form = document.getElementById('CartDrawerForm');
    if (!form) return;
    var holder = form.querySelector('[data-products]');
    if (!holder) return;
    new MutationObserver(refreshCartUi).observe(holder, { childList: true, subtree: true });
    refreshCartUi();
  }

  function initSheetControls() {
    var form = document.getElementById('CartDrawerForm');
    if (!form) return;

    // grab handle closes
    var handle = form.querySelector('.cart-sheet__handle-wrap');
    if (handle) {
      handle.addEventListener('click', function () {
        closeSheet();
      });
    }

    // promo code
    bindPromo(form.querySelector('.cart-promo'));

    var promo = null;
    if (false) {
      var toggle = promo.querySelector('.cart-promo__toggle');
      var input = promo.querySelector('.cart-promo__input');
      var apply = promo.querySelector('.cart-promo__apply');
      var chipCode = promo.querySelector('[data-promo-code]');
      var remove = promo.querySelector('.cart-promo__remove');

      if (toggle) toggle.addEventListener('click', function () { promo.classList.add('is-open'); if (input) input.focus(); });
      if (apply) apply.addEventListener('click', function () {
        var code = (input.value || '').trim();
        if (!code) return;
        promoCode = code;
        if (chipCode) chipCode.textContent = code.toUpperCase();
        promo.classList.add('has-code');
      });
      if (input) input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); apply.click(); }
      });
      if (remove) remove.addEventListener('click', function () {
        promoCode = '';
        promo.classList.remove('has-code', 'is-open');
        if (input) input.value = '';
      });
    }

    // carry promo code into checkout (runs before the theme's submit handler)
    form.addEventListener('submit', function (e) {
      if (!promoCode) return;
      var terms = form.querySelector('.cart__terms-checkbox');
      if (terms && !terms.checked) return; // let theme show its terms warning
      e.preventDefault();
      e.stopPropagation();
      window.location.href = '/discount/' + encodeURIComponent(promoCode) + '?redirect=%2Fcheckout';
    }, true);

    // upsell strip arrows + quick add
    var strip = form.querySelector('.cart-upsells__strip');
    form.querySelectorAll('.cart-upsells__arrow').forEach(function (a) {
      a.addEventListener('click', function () {
        if (!strip) return;
        strip.scrollBy({ left: a.dataset.dir === 'next' ? 220 : -220, behavior: 'smooth' });
      });
    });
    form.addEventListener('click', function (e) {
      var add = e.target.closest('[data-upsell-add]');
      if (!add) return;
      e.preventDefault();
      ajaxAdd(add.dataset.variantId, add).then(function () {
        add.classList.add('is-added');
        add.textContent = addedFlashText(add);
      }).catch(function () {});
    });
  }

  /* ============================================================
     boot + keep working after Load More / section rerenders
     ============================================================ */
  /* ============================================================
     RESILIENCE LAYER
     Guarantees the slide-up sheet even when the theme's own cart
     JS never initialized (app-embed conflicts, partial deploys,
     stores saved with Cart type = "page"). Fully inert while the
     theme's AjaxProduct + CartDrawer are alive.
     ============================================================ */
  var ccFallback = false;

  function sheetEl() { return document.getElementById('CartDrawer'); }

  function closeSheet() {
    // Theme path (no-op if nothing listens) …
    document.dispatchEvent(new CustomEvent('cart:close'));
    // … and direct fallback path.
    if (!ccFallback) return;
    var d = sheetEl();
    if (d) d.classList.remove('drawer--is-open');
    document.documentElement.classList.remove('js-drawer-open');
  }

  function rebuildFromServer() {
    var d = sheetEl();
    if (!d) return Promise.resolve();
    return fetch((routes.cartPage || '/cart') + '?view=ajax', { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var box = document.createElement('div');
        box.innerHTML = html;
        var disc = box.querySelector('.cart__discounts');
        var discTarget = d.querySelector('[data-discounts]');
        if (disc && discTarget) {
          discTarget.innerHTML = '';
          discTarget.appendChild(disc);
        }
        var prodTarget = d.querySelector('[data-products]');
        if (prodTarget) {
          prodTarget.innerHTML = '';
          while (box.firstChild) prodTarget.appendChild(box.firstChild);
        }
        refreshCartUi();
      })
      .catch(function () {});
  }

  // Never let a product form navigate. Capture phase runs before the
  // theme's own submit listener; preventDefault() kills the native
  // POST-to-/cart/add redirect in every scenario. A tick later we
  // check whether the theme's AjaxProduct took the submit (it adds
  // btn--loading synchronously) — if it did, we stand down so nothing
  // is ever added twice; if it didn't, we run the add ourselves.
  var ccLastSubmit = 0;

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.classList.contains('product-single__form')) return;
    ccLastSubmit = Date.now();
    e.preventDefault();
    setTimeout(function () {
      var btn = form.querySelector('[data-add-to-cart]') || form.querySelector('.add-to-cart');
      if (btn && btn.classList.contains('btn--loading')) return; // theme handled it
      ajaxAddForm(form, btn);
    }, 0);
  }, true);

  function ajaxAddForm(form, btn) {
    if (btn) btn.classList.add('btn--loading');
    fetch(routes.cartAdd || '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
      body: new FormData(form)
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (btn) btn.classList.remove('btn--loading');
        if (data && (data.status === 422 || data.status === 404)) {
          flashBtnText(btn, data.description || data.message || 'Unavailable');
          return;
        }
        form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: data, addToCartBtn: btn },
          bubbles: true
        }));
      })
      .catch(function () { if (btn) btn.classList.remove('btn--loading'); });
  }

  function flashBtnText(btn, msg) {
    if (!btn) return;
    var label = btn.querySelector('[data-add-to-cart-text]') || btn;
    var original = label.textContent;
    label.textContent = msg;
    setTimeout(function () { label.textContent = original; }, 2200);
  }

  // If nothing opened the sheet shortly after an add, the theme's
  // CartDrawer isn't alive — open and rebuild it ourselves.
  document.addEventListener('ajaxProduct:added', function () {
    setTimeout(function () {
      var d = sheetEl();
      if (!d) return;
      if (d.classList.contains('drawer--is-open')) {
        // Sheet already open: the theme rebuilds itself when alive; in
        // fallback mode that's on us.
        if (ccFallback) rebuildFromServer();
        return;
      }
      ccFallback = true;
      rebuildFromServer();
      d.classList.add('drawer--is-open');
      document.documentElement.classList.add('js-drawer-open');
    }, 350);
  });

  // Fallback close (button + Esc). The theme's Drawers handles these
  // itself when alive, so these only act in fallback mode.
  document.addEventListener('click', function (e) {
    if (!ccFallback) return;
    var d = sheetEl();
    if (!d || !d.classList.contains('drawer--is-open')) return;
    if (e.target.closest('.js-drawer-close')) closeSheet();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && ccFallback) closeSheet();
  });

  // Fallback quantity +/− and remove inside the sheet.
  document.addEventListener('click', function (e) {
    if (!ccFallback) return;
    var d = sheetEl();
    if (!d || !d.contains(e.target)) return;

    var adj = e.target.closest('.js-qty__adjust');
    if (adj) {
      var wrap = adj.closest('.js-qty__wrapper');
      var input = wrap && wrap.querySelector('.js-qty__num');
      if (!input) return;
      var qty = parseInt(input.value, 10) || 0;
      qty += adj.classList.contains('js-qty__adjust--plus') ? 1 : -1;
      if (qty < 0) qty = 0;
      input.value = qty;
      changeLine(input.dataset.id, qty);
      return;
    }

    var rm = e.target.closest('.cart__remove a');
    if (rm) {
      e.preventDefault();
      var m = /id=([^&]+)/.exec(rm.getAttribute('href') || '');
      if (m) changeLine(decodeURIComponent(m[1]), 0);
    }
  });

  function changeLine(key, qty) {
    if (!key) return;
    fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(function () { rebuildFromServer(); })
      .catch(function () {});
  }

  /* ============================================================
     AUTO UPSELLS
     Zero-config "You might like": when no upsell collection is set,
     the strip fills itself from Shopify's native product
     recommendations for whatever is in the cart, falling back to
     the store's newest products. In both auto and curated modes,
     products already in the cart are hidden from the strip.
     ============================================================ */
  var upsellPool = [];
  var upsellAutoLoaded = false;
  var upsellLoading = false;
  var upsellLoadedWithItems = false;
  var cartSeeded = false;
  var inCartProducts = {};
  var inCartVariants = {};

  function currentCartCount() {
    var form = document.getElementById('CartDrawerForm');
    var items = form && form.querySelector('.cart__items');
    return items ? parseInt(items.dataset.count || '0', 10) : 0;
  }

  function homeUrl() {
    var h = routes.home || '/';
    if (h.charAt(h.length - 1) !== '/') h += '/';
    return h;
  }

  function fetchJson(url) {
    return fetch(url, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function upsellRoot() {
    var form = document.getElementById('CartDrawerForm');
    return form ? form.querySelector('.cart-upsells') : null;
  }

  // Track cart contents (variant + product ids) so upsells can be deduped.
  function syncInCartVariantsFromDom() {
    var form = document.getElementById('CartDrawerForm');
    if (!form) return;
    form.querySelectorAll('[data-products] .js-qty__num[data-id]').forEach(function (inp) {
      var vid = String(inp.dataset.id || '').split(':')[0];
      if (vid) inCartVariants[vid] = true;
    });
  }

  function seedCartJson() {
    if (cartSeeded) return Promise.resolve([]);
    cartSeeded = true;
    return fetchJson(routes.cart || (homeUrl() + 'cart.js')).then(function (cart) {
      var items = (cart && cart.items) || [];
      items.forEach(function (it) {
        if (it.product_id) inCartProducts[it.product_id] = true;
        if (it.id) inCartVariants[it.id] = true;
      });
      return items;
    });
  }

  document.addEventListener('ajaxProduct:added', function (evt) {
    var p = evt && evt.detail && evt.detail.product;
    if (!p) return;
    if (p.product_id) inCartProducts[p.product_id] = true;
    if (p.id) inCartVariants[p.id] = true;
  });

  function normalizeRecProduct(p, home) {
    var vars = p.variants || [];
    var single = p.has_only_default_variant ||
      (vars.length === 1 && vars[0].title === 'Default Title');
    var avail = (p.available !== undefined)
      ? p.available
      : vars.some(function (v) { return v.available; });
    var img = p.featured_image || (p.images && p.images[0]) || '';
    if (img && img.src) img = img.src;
    var price = p.price;
    if (typeof price === 'string') price = Math.round(parseFloat(price) * 100);
    return {
      id: p.id,
      title: p.title,
      url: p.url || (home + 'products/' + p.handle),
      image: img,
      priceCents: price || 0,
      singleVariantId: (single && vars[0]) ? vars[0].id : null,
      available: !!avail
    };
  }

  function normalizeJsonProduct(p, home) {
    var vars = p.variants || [];
    var single = vars.length === 1 && vars[0].title === 'Default Title';
    var avail = vars.some(function (v) { return v.available !== false; });
    var minCents = 0;
    vars.forEach(function (v) {
      var c = Math.round(parseFloat(v.price) * 100);
      if (!isNaN(c) && (minCents === 0 || c < minCents)) minCents = c;
    });
    var img = (p.images && p.images[0] && (p.images[0].src || p.images[0])) || '';
    return {
      id: p.id,
      title: p.title,
      url: home + 'products/' + p.handle,
      image: img,
      priceCents: minCents,
      singleVariantId: (single && vars[0]) ? vars[0].id : null,
      available: avail
    };
  }

  function loadUpsellPool() {
    var home = homeUrl();
    var curated = configuredUpsellCollection();
    if (curated) {
      return fetchJson(home + 'collections/' + curated + '/products.json?limit=12').then(function (d) {
        var acc = [];
        ((d && d.products) || []).forEach(function (p) { acc.push(normalizeJsonProduct(p, home)); });
        if (acc.length) return acc;
        return loadUpsellPoolAuto();
      });
    }
    return loadUpsellPoolAuto();
  }

  function loadUpsellPoolAuto() {
    var home = homeUrl();
    return fetchJson(routes.cart || (home + 'cart.js')).then(function (cart) {
      var items = (cart && cart.items) || [];
      items.forEach(function (it) {
        if (it.product_id) inCartProducts[it.product_id] = true;
        if (it.id) inCartVariants[it.id] = true;
      });
      var pids = [];
      items.forEach(function (it) {
        if (it.product_id && pids.indexOf(it.product_id) === -1) pids.push(it.product_id);
      });
      var chain = Promise.resolve([]);
      pids.slice(0, 3).forEach(function (pid) {
        chain = chain.then(function (acc) {
          if (acc.length >= 8) return acc;
          return fetchJson(home + 'recommendations/products.json?product_id=' + pid + '&limit=10')
            .then(function (d) {
              ((d && d.products) || []).forEach(function (p) {
                if (!acc.some(function (a) { return a.id === p.id; })) {
                  acc.push(normalizeRecProduct(p, home));
                }
              });
              return acc;
            });
        });
      });
      return chain.then(function (acc) {
        if (acc.length >= 4) return acc;
        return fetchJson(home + 'products.json?limit=24').then(function (d) {
          ((d && d.products) || []).forEach(function (p) {
            var n = normalizeJsonProduct(p, home);
            if (!acc.some(function (a) { return a.id === n.id; })) acc.push(n);
          });
          return acc;
        });
      });
    });
  }

  function escapeHtml(s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }

  function upsellCardHtml(u, labels) {
    var img = u.image
      ? '<img class="upsell-card__img" src="' + escapeHtml(u.image) + '" alt="' + escapeHtml(u.title) + '" loading="lazy">'
      : '<span class="upsell-card__img upsell-card__img--blank" aria-hidden="true"></span>';
    var btn = u.singleVariantId
      ? '<button type="button" class="upsell-card__btn" data-upsell-add data-variant-id="' + escapeHtml(u.singleVariantId) + '"><span aria-hidden="true">+</span> ' + escapeHtml(labels.add) + '</button>'
      : '<a class="upsell-card__btn" href="' + escapeHtml(u.url) + '">' + escapeHtml(labels.see) + ' <span aria-hidden="true">&rsaquo;</span></a>';
    return '<div class="upsell-card" data-product-id="' + escapeHtml(u.id) + '">' + img +
      '<div class="upsell-card__info"><a class="upsell-card__name" href="' + escapeHtml(u.url) + '">' + escapeHtml(u.title) + '</a>' +
      '<div class="upsell-card__price">' + money(u.priceCents) + '</div></div>' + btn + '</div>';
  }

  function renderAutoUpsells() {
    var root = upsellRoot();
    if (!root || !root.hasAttribute('data-upsell-auto')) return;
    var strip = root.querySelector('.cart-upsells__strip');
    if (!strip) return;
    var limit = parseInt(root.dataset.upsellLimit, 10) || 8;
    var labels = {
      add: root.dataset.addLabel || 'Add',
      see: root.dataset.seeLabel || 'See product'
    };
    var html = '';
    var count = 0;
    for (var i = 0; i < upsellPool.length && count < limit; i++) {
      var u = upsellPool[i];
      if (!u.available) continue;
      if (inCartProducts[u.id]) continue;
      if (u.singleVariantId && inCartVariants[u.singleVariantId]) continue;
      html += upsellCardHtml(u, labels);
      count++;
    }
    strip.innerHTML = html;
    root.classList.toggle('cart-upsells--pending', count === 0);
  }

  function upsellsSync() {
    var root = upsellRoot();
    if (!root) return;
    syncInCartVariantsFromDom();

    if (root.hasAttribute('data-upsell-auto')) {
      var hasItems = currentCartCount() > 0;
      var needLoad = !upsellAutoLoaded || (!upsellLoadedWithItems && hasItems);
      if (needLoad && !upsellLoading) {
        upsellLoading = true;
        upsellAutoLoaded = true;
        upsellLoadedWithItems = hasItems;
        loadUpsellPool().then(function (pool) {
          upsellPool = pool || [];
          upsellLoading = false;
          renderAutoUpsells();
        });
      } else if (!upsellLoading) {
        renderAutoUpsells();
      }
      return;
    }

    // Curated (collection) mode: hide anything already in the cart.
    seedCartJson().then(function () {
      var visible = 0;
      root.querySelectorAll('.upsell-card').forEach(function (card) {
        var pid = card.dataset.productId;
        var addBtn = card.querySelector('[data-upsell-add]');
        var vid = addBtn ? addBtn.dataset.variantId : null;
        var inCart = (pid && inCartProducts[pid]) || (vid && inCartVariants[vid]);
        card.classList.toggle('is-in-cart', !!inCart);
        if (!inCart) visible++;
      });
      root.classList.toggle('cart-upsells--pending', visible === 0);
    });
  }

  /* ============================================================
     SECTION CONFIG LAYER
     Reads #cc-config emitted by sections/conversion-booster.liquid
     (statically rendered on every page) and applies it at boot.
     Section values override the global Theme settings versions.
     Absent config = zero behavior change.
     ============================================================ */
  var CFG = null;

  // Animations self-disable when the OS asks for reduced motion. That's
  // correct default behaviour, but it also means a merchant whose own
  // machine has "reduce motion" on never sees them. The section setting
  // "Always show animations" overrides it.
  function motionOK() {
    if (CFG && CFG.forceMotion) return true;
    return !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function cfgLabel(key) {
    return (CFG && CFG.labels && CFG.labels[key]) || null;
  }

  function readConfig() {
    var el = document.getElementById('cc-config');
    if (!el) return;
    try { CFG = JSON.parse(el.textContent); } catch (e) { CFG = null; }
    if (!CFG) return;

    if (CFG.threshold) THRESHOLD = CFG.threshold * 100;

    var en = CFG.enabled || {};
    var H = document.documentElement.classList;
    if (en.quickAdd === false) H.add('cc-off-quick-add');
    if (en.stickyAtc === false) H.add('cc-off-sticky-atc');
    if (en.trustRows === false) H.add('cc-off-trust-rows');
    if (en.detailsPanel === false) H.add('cc-off-details-panel');
    if (en.shipMeter === false) H.add('cc-off-ship-meter');
    if (en.promo === false) H.add('cc-off-promo');
    if (en.upsells === false) H.add('cc-off-upsells');

    if (CFG.forceMotion) document.documentElement.classList.add('cc-force-motion');

    if (CFG.cartStyle === 'side') {
      H.add('cc-cart-side');
      var d = document.getElementById('CartDrawer');
      if (d) d.classList.remove('drawer--bottom');
    }
  }

  function setText(sel, txt, root) {
    if (!txt) return;
    var el = (root || document).querySelector(sel);
    if (el) el.textContent = txt;
  }

  function applyConfigTexts() {
    if (!CFG) return;
    var form = document.getElementById('CartDrawerForm');
    if (form) {
      var meter = form.querySelector('.ship-meter');
      if (meter) {
        if (cfgLabel('progress')) meter.dataset.progressText = cfgLabel('progress');
        if (cfgLabel('achieved')) meter.dataset.achievedText = cfgLabel('achieved');
      }
      setText('.cart-sheet__checkout > span:first-child', cfgLabel('checkout'), form);
      setText('.cart-promo__toggle span:last-child', cfgLabel('promoToggle'), form);
      var pin = form.querySelector('.cart-promo__input');
      if (pin && cfgLabel('promoPlaceholder')) pin.placeholder = cfgLabel('promoPlaceholder');
      setText('.cart-promo__apply', cfgLabel('promoApply'), form);
      form.querySelectorAll('.cart-upsells').forEach(function (u) {
        setText('.cart-upsells__title', cfgLabel('upsellHeading'), u);
        if (cfgLabel('upsellAdd')) u.dataset.addLabel = cfgLabel('upsellAdd');
        if (cfgLabel('upsellSee')) u.dataset.seeLabel = cfgLabel('upsellSee');
        if (cfgLabel('added')) u.dataset.addedText = cfgLabel('added');
      });
    }
    var rows = document.querySelectorAll('.buybox-trust__row span');
    ['trust1', 'trust2', 'trust3'].forEach(function (k, i) {
      if (rows[i] && cfgLabel(k)) rows[i].textContent = cfgLabel(k);
    });
    setText('.buybox-trust__details span:first-child', cfgLabel('seeDetails'));
    setText('.details-panel__title', cfgLabel('detailsHeading'));

    document.querySelectorAll('.quick-add').forEach(function (qa) {
      if (cfgLabel('lock')) qa.dataset.lockText = cfgLabel('lock');
      if (cfgLabel('quickAdd')) qa.dataset.unlockText = cfgLabel('quickAdd');
      if (cfgLabel('added')) qa.dataset.addedText = cfgLabel('added');
      setText('.quick-add__size-label', cfgLabel('selectSize'), qa);
      if (qa.dataset.locked !== 'true' && cfgLabel('quickAdd')) {
        var atc = qa.querySelector('[data-qa-atc]');
        if (atc) (atc.querySelector('span') || atc).textContent = cfgLabel('quickAdd');
      }
    });
    ['.sticky-atc', '.details-panel'].forEach(function (sel) {
      var el = document.querySelector(sel);
      if (el && cfgLabel('added')) el.dataset.addedText = cfgLabel('added');
    });
    if (cfgLabel('countdown')) {
      var lbls = cfgLabel('countdown').split(',');
      document.querySelectorAll('.drop-countdown').forEach(function (cd) {
        cd.querySelectorAll('.drop-countdown__lbl').forEach(function (l, i) {
          if (lbls[i]) l.textContent = lbls[i].trim();
        });
      });
    }
  }

  function countdownHtml() {
    var lbls = (cfgLabel('countdown') || 'Days,Hrs,Min,Sec').split(',');
    var out = '<div class="drop-countdown" aria-label="Time until this drop unlocks">';
    for (var i = 0; i < 4; i++) {
      out += '<span class="drop-countdown__unit"><span class="drop-countdown__num">--</span>' +
        '<span class="drop-countdown__lbl">' + escapeHtml((lbls[i] || '').trim()) + '</span></span>';
    }
    return out + '</div>';
  }

  function handleFromLink(card) {
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var h = (link.getAttribute('href') || '').split('/products/')[1];
    if (!h) return null;
    return h.split('?')[0].split('#')[0].replace(/\/+$/, '');
  }

  function applyConfigDrops(root) {
    if (!CFG || !CFG.drops || !CFG.drops.length) return;

    // Collection / grid cards
    (root || document).querySelectorAll('.quick-add:not([data-drop-init])').forEach(function (qa) {
      if (qa.dataset.locked === 'true') return;
      var card = qa.closest('.grid-product');
      if (!card) return;
      var handle = handleFromLink(card);
      if (!handle) return;
      var drop = null;
      for (var i = 0; i < CFG.drops.length; i++) {
        if (CFG.drops[i].handle === handle) drop = CFG.drops[i];
      }
      if (!drop) return;
      qa.dataset.locked = 'true';
      qa.classList.add('cc-config-locked');
      if (drop.deadline) {
        qa.dataset.deadline = String(drop.deadline);
        if (!qa.querySelector('.drop-countdown')) {
          var box = document.createElement('div');
          box.innerHTML = countdownHtml();
          qa.insertBefore(box.firstChild, qa.firstChild);
        }
      }
      var atc = qa.querySelector('[data-qa-atc]');
      if (atc) {
        atc.classList.add('quick-add__atc--locked');
        atc.disabled = true;
        (atc.querySelector('span') || atc).textContent =
          cfgLabel('lockButton') || 'Locked — dropping soon';
      }
    });

    // Product page
    var m = location.pathname.match(/\/products\/([^\/?#]+)/);
    if (!m || document.querySelector('[data-pdp-lock]')) return;
    var drop = null;
    for (var j = 0; j < CFG.drops.length; j++) {
      if (CFG.drops[j].handle === m[1]) drop = CFG.drops[j];
    }
    if (!drop) return;
    var note = document.createElement('div');
    note.className = 'pdp-drop-note';
    note.setAttribute('data-pdp-lock', '');
    if (drop.deadline) note.setAttribute('data-deadline', String(drop.deadline));
    note.innerHTML = '<span class="cc-label">' +
      escapeHtml(cfgLabel('lock') || 'Coming soon') + ' — ' +
      escapeHtml(cfgLabel('pdpNote') || 'unlocks in') + '</span>' +
      (drop.deadline ? countdownHtml() : '');
    var anchor = document.querySelector('.buybox-trust') ||
      document.querySelector('.product-section [data-add-to-cart]');
    if (anchor) anchor.parentNode.insertBefore(note, anchor);
  }

  function configuredUpsellCollection() {
    return (CFG && CFG.upsellCollection) || null;
  }

  /* ============================================================
     VARIANT LABEL: "COLOR — Vintage Red"  ->  "COLOR: VINTAGE RED"
     Done here so it works without editing snippets/variant-button.liquid.
     ============================================================ */
  function fixVariantLabels() {
    document.querySelectorAll('.variant__label-info').forEach(function (info) {
      if (info.dataset.ccFixed) return;
      // Keep ONLY the value element, dropping the separator text node.
      var val = info.querySelector('[data-variant-color-label]');
      if (!val) return;
      info.dataset.ccFixed = '1';
      while (info.firstChild) info.removeChild(info.firstChild);
      info.appendChild(val);

      // "Color" -> "Color:" on the parent label, once.
      var host = info.closest('.variant__label');
      if (host && !host.dataset.ccColon) {
        host.dataset.ccColon = '1';
        for (var j = 0; j < host.childNodes.length; j++) {
          var t = host.childNodes[j];
          if (t.nodeType === 3 && t.nodeValue.trim()) {
            t.nodeValue = t.nodeValue.trim() + ': ';
            break;
          }
        }
      }
    });
  }

  /* ============================================================
     SAFETY NET: keep the storefront usable if theme.js fails
     ------------------------------------------------------------
     If the theme's own inline setup script errors, <html> keeps the
     no-js class (the fallback variant <select> appears and JS-only
     UI stays hidden) and theme.Drawers never binds — leaving the
     mobile menu icon looking fine but doing nothing when tapped.
     Both are repaired here. Inert when the theme is healthy.
     ============================================================ */
  (function repairNoJs() {
    var h = document.documentElement;
    if (h.classList.contains('no-js')) {
      h.classList.remove('no-js');
      h.classList.add('js');
    }
  })();

  // Mobile nav: bind only if nothing else opened the drawer.
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('.js-drawer-open-nav, [aria-controls="NavDrawer"]');
    if (!trigger) return;
    var drawer = document.getElementById('NavDrawer');
    if (!drawer) return;

    setTimeout(function () {
      if (drawer.classList.contains('drawer--is-open')) return; // theme handled it
      drawer.classList.add('drawer--is-open');
      document.documentElement.classList.add('js-drawer-open');
      drawer.setAttribute('aria-hidden', 'false');
    }, 60);
  }, false);

  // …and let it close again in that same fallback case.
  document.addEventListener('click', function (e) {
    var drawer = document.getElementById('NavDrawer');
    if (!drawer || !drawer.classList.contains('drawer--is-open')) return;
    var closer = e.target.closest('.js-drawer-close');
    var outside = !drawer.contains(e.target) &&
      !e.target.closest('.js-drawer-open-nav, [aria-controls="NavDrawer"]');
    if (closer || outside) {
      drawer.classList.remove('drawer--is-open');
      document.documentElement.classList.remove('js-drawer-open');
      drawer.setAttribute('aria-hidden', 'true');
    }
  }, false);

  /* ---------- locked-drop notify button ---------- */
  /* ------------------------------------------------------------
     Shopify Forms placement
     Add the Shopify Forms block once to the product template. On a
     locked drop it relocates into the notify box under the countdown;
     on every other product it's hidden, so one block serves all
     products without appearing where it shouldn't. Turn off with
     "Signup form only on locked drops" to leave it where you put it.
     ------------------------------------------------------------ */
  function isDropLocked() {
    if (document.querySelector('[data-pdp-lock]')) return true;
    var m = location.pathname.match(/\/products\/([^\/?#]+)/);
    if (m && CFG && CFG.drops) {
      for (var i = 0; i < CFG.drops.length; i++) {
        if (CFG.drops[i].handle === m[1]) return true;
      }
    }
    return false;
  }

  function formBlocks() {
    var out = [];
    var slot = document.querySelector('[data-drop-notify]');
    var nodes = document.querySelectorAll(
      '.shopify-app-block, [id^="shopify-block"], shopify-form, [data-shopify-forms]');
    Array.prototype.forEach.call(nodes, function (el) {
      if (slot && slot.contains(el)) return;

      // NEVER touch anything containing the buy form — a bare "form"
      // match hid the add-to-cart button when it sat inside a block
      // wrapper. Require a real signup input instead.
      if (el.querySelector(
            '.product-single__form, [data-add-to-cart], [name="id"], ' +
            '[name="add"], .add-to-cart, .shopify-payment-button')) return;
      if (el.closest('.product-single__form')) return;
      if (!el.querySelector('input[type="email"], input[type="tel"]')) return;

      // If a Form ID is configured, only adopt the block holding THAT
      // form — so a store with several Shopify Forms picks the right one.
      var wantId = cfgLabel('notifyFormId');
      if (wantId) {
        var hay = (el.getAttribute('id') || '') + ' ' + (el.className || '') + ' ' +
                  (el.getAttribute('data-form-id') || '') + ' ' + (el.innerHTML || '').slice(0, 4000);
        if (hay.indexOf(String(wantId)) === -1) return;
      }

      out.push(el);
    });
    return out;
  }

  function manageShopifyForm() {
    if (!/\/products\//.test(location.pathname)) return true;
    if (CFG && CFG.formsLockedOnly === false) return true;

    var slot = document.querySelector('[data-drop-notify]');
    var locked = isDropLocked();
    var blocks = formBlocks();

    if (slot && slot.querySelector('.drop-notify__form') &&
        slot.querySelector('.drop-notify__form').children.length) return true;
    if (!blocks.length) return false;

    blocks.forEach(function (el) {
      el.setAttribute('data-cc-form-block', '');
      if (locked && slot) {
        var host = slot.querySelector('.drop-notify__form');
        if (!host) {
          host = document.createElement('div');
          host.className = 'drop-notify__form';
          slot.appendChild(host);
        }
        el.removeAttribute('hidden');
        el.style.display = '';
        host.appendChild(el);
        var b = slot.querySelector('[data-drop-notify-btn]');
        if (b) b.setAttribute('hidden', 'hidden');
      } else {
        el.setAttribute('hidden', 'hidden');
      }
    });
    return true;
  }

  function initDropNotify() {
    // Flag the page so CSS can swap the buy area for the signup form.
    if (document.querySelector('[data-pdp-lock]')) {
      document.documentElement.classList.add('cc-drop-locked');
    }
    var slot = document.querySelector('[data-drop-notify]');
    if (!slot) {
      // unlocked product: hide the shared form block
      manageShopifyForm();
      setTimeout(manageShopifyForm, 800);
      setTimeout(manageShopifyForm, 2000);
    }
    if (slot) {
      // app blocks can mount late
      manageShopifyForm();
      setTimeout(manageShopifyForm, 800);
      // place the signup where the add-to-cart button sits
      var buyForm = document.querySelector('.product-single__form');
      if (buyForm && buyForm.parentNode && !slot.dataset.ccMoved) {
        slot.dataset.ccMoved = '1';
        buyForm.parentNode.insertBefore(slot, buyForm.nextSibling);
      }
      setTimeout(function () {
        if (!manageShopifyForm()) {
          var b = slot.querySelector('[data-drop-notify-btn][data-needs-setup]');
          if (b) slot.style.display = 'none';
        }
      }, 2000);
    }
    var btn = document.querySelector('[data-drop-notify-btn]');
    if (!btn) return;

    // section settings override the theme-settings version
    var wrap = document.querySelector('[data-drop-notify]');
    if (wrap) {
      setText('.drop-notify__title', cfgLabel('notifyHeading'), wrap);
      setText('.drop-notify__text', cfgLabel('notifyText'), wrap);
      if (cfgLabel('notifyLabel')) btn.textContent = cfgLabel('notifyLabel');
      if (cfgLabel('notifyUrl')) {
        btn.setAttribute('href', cfgLabel('notifyUrl'));
        btn.removeAttribute('data-inbox-only');
      }
    }
    var inboxOnly = btn.hasAttribute('data-inbox-only');

    function openInbox() {
      // Shopify Inbox exposes one of these once its embed loads.
      if (window.ShopifyChat && typeof window.ShopifyChat.open === 'function') {
        window.ShopifyChat.open(); return true;
      }
      var launcher = document.querySelector(
        '#ShopifyChat, .shopify-chat, [id^="shopify-chat"], shopify-chat-button');
      if (launcher) {
        (launcher.querySelector('button') || launcher).click();
        return true;
      }
      return false;
    }

    // No URL set and no Inbox on the page -> nothing to click, so hide it.
    if (inboxOnly && !openInbox.available) {
      setTimeout(function () {
        if (!document.querySelector('#ShopifyChat, .shopify-chat, [id^="shopify-chat"], shopify-chat-button') &&
            !window.ShopifyChat) {
          var wrap = document.querySelector('[data-drop-notify]');
          if (wrap) wrap.style.display = 'none';
        }
      }, 1500);
    }

    btn.addEventListener('click', function (e) {
      if (!inboxOnly) return;      // a real URL was configured; let it navigate
      e.preventDefault();
      openInbox();
    });
  }

  /* ============================================================
     PRODUCT OPTIONS — self-contained
     theme.Variants is easily knocked out (missing variant JSON, a
     broken inline script, an app conflict), and when it dies the
     option buttons stop showing sold-out and colours stop switching
     the gallery. This runs the same logic from our own file so both
     work regardless. Idempotent: if theme.js is alive it computes
     the same result rather than fighting it.
     ============================================================ */
  var ccVariants = null;

  function readProductVariants() {
    var el = document.querySelector('[data-variant-json]');
    if (!el) return null;
    try {
      var v = JSON.parse(el.textContent);
      return (v && v.length) ? v : null;
    } catch (e) { return null; }
  }

  function optionWraps() {
    return document.querySelectorAll('.product-section .variant-input-wrap, [data-section-type="product"] .variant-input-wrap');
  }

  function currentSelections(wraps) {
    var sel = {};
    Array.prototype.forEach.call(wraps, function (w) {
      var checked = w.querySelector('input:checked');
      if (checked && w.dataset.index) sel[w.dataset.index] = checked.value;
    });
    return sel;
  }

  function variantMatches(v, n, val, sel) {
    if (v['option' + n] !== val) return false;
    // cascade: honour the options chosen BEFORE this one (e.g. colour
    // constrains sizes) and ignore later ones, matching Shopify's own
    // behaviour so a colour isn't hidden by the size you happen to have.
    for (var i = 1; i < n; i++) {
      var key = 'option' + i;
      if (sel[key] != null && v[key] !== sel[key]) return false;
    }
    return true;
  }

  // Keep the server-rendered --active class in step with the real
  // selection; the theme never moves it on its own.
  /* :has() isn't available on older Safari, so the swatch/size layout
     rules would be dropped there. Tagging the same elements with plain
     classes gives those browsers an identical result. */
  function tagOptionKinds() {
    document.querySelectorAll('.variant-input-wrap').forEach(function (w) {
      if (w.querySelector('.swatch-photo-rect')) w.classList.add('cc-has-swatches');
    });
    document.querySelectorAll('.variant-input').forEach(function (vi) {
      var l = vi.querySelector('label');
      if (!l) return;
      if (!l.classList.contains('swatch-photo-rect') && !l.classList.contains('color-swatch')) {
        vi.classList.add('cc-text-option');
      }
    });
    document.querySelectorAll('[data-product-inventory]').forEach(function (t) {
      var row = (t.closest && (t.closest('.sales-point') || t.closest('.icon-and-text'))) || t.parentNode;
      if (row && row.classList) row.classList.add('cc-has-stock');
    });
  }

  function syncSwatchActive() {
    tagOptionKinds();
    document.querySelectorAll('.variant-input').forEach(function (vi) {
      var input = vi.querySelector('input');
      var label = vi.querySelector('label');
      if (!input || !label) return;
      if (label.classList.contains('swatch-photo-rect')) {
        label.classList.toggle('swatch-photo-rect--active', input.checked);
      }
      if (label.classList.contains('color-swatch')) {
        label.classList.toggle('color-swatch--active', input.checked);
      }
    });
  }

  function markAvailability() {
    syncSwatchActive();
    if (!ccVariants) return;
    var wraps = optionWraps();
    if (!wraps.length) return;
    var sel = currentSelections(wraps);

    Array.prototype.forEach.call(wraps, function (w) {
      var n = parseInt(String(w.dataset.index || 'option1').replace('option', ''), 10);
      w.querySelectorAll('.variant-input').forEach(function (vi) {
        var val = vi.dataset.value;
        var ok = ccVariants.some(function (v) {
          return v.available && variantMatches(v, n, val, sel);
        });
        var input = vi.querySelector('input');
        var label = vi.querySelector('label');
        if (input) input.classList.toggle('disabled', !ok);
        if (label) label.classList.toggle('disabled', !ok);
      });
    });
  }

  function resolveCurrentVariant(sel) {
    if (!ccVariants) return null;
    for (var i = 0; i < ccVariants.length; i++) {
      var v = ccVariants[i], hit = true;
      for (var k in sel) {
        var n = parseInt(k.replace('option', ''), 10);
        if (v['option' + n] !== sel[k]) { hit = false; break; }
      }
      if (hit) return v;
    }
    return null;
  }

  /* colour -> gallery image (uses the server-built slide map) */
  var ccVariantMedia = null;

  function readVariantMedia() {
    var el = document.querySelector('[data-cc-variant-media]');
    if (!el) return null;
    try {
      var m = JSON.parse(el.textContent);
      return (m && Object.keys(m).length) ? m : null;
    } catch (e) { return null; }
  }

  function galleryItems() {
    // This theme ships TWO product galleries. product-images-grid.liquid
    // (a horizontal scroller of .pg-item) is the one actually rendered by
    // product-template; product-images.liquid (.product-main-slide) is the
    // slideshow. Support both so the switch works either way.
    var grid = document.querySelectorAll('.product-grid-gallery .pg-item, .pg-wrap .pg-item');
    if (grid.length) return { type: 'grid', items: grid };
    var slides = document.querySelectorAll('.product-main-slide');
    if (slides.length) return { type: 'slideshow', items: slides };
    return null;
  }

  function switchVariantImage(variant) {
    if (!variant) return;
    var g = galleryItems();
    if (!g) return;

    // index of this variant's image within product.media
    var idx = null;
    if (ccVariantMedia && ccVariantMedia[variant.id] != null) {
      idx = ccVariantMedia[variant.id];
    } else {
      // fall back to matching the media id straight off the element
      var mid = (variant.featured_media && variant.featured_media.id) ||
                (variant.featured_image && variant.featured_image.id);
      if (mid != null) {
        for (var i = 0; i < g.items.length; i++) {
          var raw = g.items[i].getAttribute('data-media-id') || '';
          if (raw === String(mid) || raw.slice(-String(mid).length - 1) === '-' + mid) { idx = i; break; }
        }
      }
    }
    if (idx == null) return;

    var target = g.items[idx];
    if (!target) {
      target = document.querySelector('.pg-item[data-media-id="' + idx + '"]') ||
               document.querySelector('.product-main-slide[data-index="' + idx + '"]');
    }
    if (!target) return;

    if (g.type === 'grid') {
      var gallery = target.closest('.product-grid-gallery') || target.parentNode;
      if (!gallery) return;
      // horizontal scroller: bring the image into view and light its dot
      var left = target.offsetLeft - gallery.offsetLeft;
      try {
        gallery.scrollTo({ left: left, behavior: 'smooth' });
      } catch (e) {
        gallery.scrollLeft = left;   // older engines reject the object form
      }
      var wrap = gallery.closest('.pg-wrap') || document;
      var dots = wrap.querySelectorAll('.pg-dot');
      Array.prototype.forEach.call(dots, function (d, n) {
        d.classList.toggle('is-active', n === idx);
      });
      return;
    }

    // slideshow layout
    var slide = target, parent = slide.parentNode;
    if (!parent) return;
    if (window.Flickity && parent.classList.contains('flickity-enabled')) {
      var f = window.Flickity.data(parent);
      if (f) { try { f.selectCell(slide); return; } catch (e) {} }
    }
    parent.classList.add('cc-manual-gallery');
    Array.prototype.forEach.call(parent.children, function (c) {
      if (c.classList && c.classList.contains('product-main-slide')) {
        c.classList.toggle('cc-slide-active', c === slide);
      }
    });
  }

  function syncFormVariant(variant) {
    if (!variant) return;
    var id = String(variant.id);
    var sel = document.querySelector('[data-product-select], select[name="id"]');
    if (sel) {
      // Only assign when the option exists — setting a select to a value
      // it doesn't have silently blanks it, which would post an empty
      // variant id and break add to cart.
      var has = false;
      for (var i = 0; i < sel.options.length; i++) {
        if (sel.options[i].value === id) { has = true; break; }
      }
      if (has && sel.value !== id) sel.value = id;
    }
    var hidden = document.querySelector('.product-single__form input[name="id"]');
    if (hidden) hidden.value = id;
  }

  function initProductOptions() {
    ccVariants = readProductVariants();
    ccVariantMedia = readVariantMedia();
    if (!ccVariants) return;
    var wraps = optionWraps();
    if (!wraps.length) return;

    markAvailability();

    // set image + stock label for whatever is selected on load
    var ccStart = resolveCurrentVariant(currentSelections(wraps));
    if (ccStart) { switchVariantImage(ccStart); updateInventory(ccStart); }

    Array.prototype.forEach.call(wraps, function (w) {
      w.addEventListener('change', function () {
        syncSwatchActive();
        markAvailability();
        var v = resolveCurrentVariant(currentSelections(optionWraps()));
        if (!v) return;
        switchVariantImage(v);
        syncFormVariant(v);
        updateInventory(v);
        document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: v } }));
      });
    });
  }

  /* ============================================================
     SELF-DIAGNOSTIC
     Add ?ccdebug=1 to any product URL to see an on-screen report of
     exactly what this script found. Also logged to the console on
     every page as one line, so the build in use is always verifiable.
     ============================================================ */
  /* ------------------------------------------------------------
     Stray vertical line remover.
     Kills any thin, tall, dark element (or a dark left border on a
     tall block) that isn't part of known UI. Deliberately narrow
     criteria — 3px or less wide, 250px or taller, dark, visible, and
     outside drawers/menus/SVG — so real dividers and borders survive.
     ------------------------------------------------------------ */
  function ccIsDark(c) {
    var m = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/.exec(c || '');
    if (!m) return false;
    if (m[4] !== undefined && parseFloat(m[4]) < 0.2) return false;
    return (+m[1] + +m[2] + +m[3]) < 300;
  }

  function ccDescribe(el) {
    var s = el.tagName.toLowerCase();
    if (el.id) s += '#' + el.id;
    if (el.className && typeof el.className === 'string') {
      s += '.' + el.className.trim().split(/\s+/).slice(0, 3).join('.');
    }
    return s;
  }

  var ccKilledLines = [];

  function killStrayLines() {
    if (!document.body) return;
    var SAFE = '#NavDrawer, #CartDrawer, .drawer, .details-panel, .mobile-nav, ' +
               '.site-header, .sticky-atc, .trust-bar, .noUi-target, svg, ' +
               '.shopify-app-block, .drop-notify';
    var all = document.body.querySelectorAll('div, span, hr, aside, i, b');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.dataset.ccLineChecked) continue;
      el.dataset.ccLineChecked = '1';
      if (el.closest && el.closest(SAFE)) continue;

      var r = el.getBoundingClientRect();
      if (r.height < 250) continue;

      var cs = getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') continue;

      // 1) a thin dark element = the line itself
      if (r.width > 0.4 && r.width <= 3 && ccIsDark(cs.backgroundColor)) {
        el.style.setProperty('display', 'none', 'important');
        ccKilledLines.push(ccDescribe(el) + ' [w=' + r.width.toFixed(1) + ']');
        continue;
      }

      // 2) a dark left/right border on a tall block
      var blw = parseFloat(cs.borderLeftWidth) || 0;
      if (blw > 0 && blw <= 3 && r.width > 40 && ccIsDark(cs.borderLeftColor)) {
        el.style.setProperty('border-left', '0', 'important');
        ccKilledLines.push(ccDescribe(el) + ' (border-left)');
      }
      var brw = parseFloat(cs.borderRightWidth) || 0;
      if (brw > 0 && brw <= 3 && r.width > 40 && ccIsDark(cs.borderRightColor)) {
        el.style.setProperty('border-right', '0', 'important');
        ccKilledLines.push(ccDescribe(el) + ' (border-right)');
      }
    }
  }

  /* Finds thin, tall, dark elements — the shape of a stray vertical
     line. Reports a readable path so the culprit can be named instead
     of guessed at. */
  function findStrayLines() {
    var out = [];
    try {
      var all = document.body.querySelectorAll('*');
      for (var i = 0; i < all.length && out.length < 6; i++) {
        var el = all[i];
        var r = el.getBoundingClientRect();
        if (r.width > 6 || r.width < 0.5) continue;
        if (r.height < 200) continue;
        var cs = getComputedStyle(el);
        if (cs.visibility === 'hidden' || cs.display === 'none' || cs.opacity === '0') continue;
        var bg = cs.backgroundColor || '';
        var bl = cs.borderLeftWidth !== '0px' ? cs.borderLeftColor : '';
        var dark = function (c) {
          var m = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/.exec(c || '');
          if (!m) return false;
          if (m[4] !== undefined && parseFloat(m[4]) < 0.15) return false;
          return (+m[1] + +m[2] + +m[3]) < 300;
        };
        if (!dark(bg) && !dark(bl)) continue;

        var path = el.tagName.toLowerCase();
        if (el.id) path += '#' + el.id;
        if (el.className && typeof el.className === 'string') {
          path += '.' + el.className.trim().split(/\s+/).slice(0, 3).join('.');
        }
        var p = el.parentElement;
        if (p) {
          var pp = p.tagName.toLowerCase();
          if (p.id) pp += '#' + p.id;
          else if (p.className && typeof p.className === 'string') {
            pp += '.' + p.className.trim().split(/\s+/).slice(0, 2).join('.');
          }
          path = pp + ' > ' + path;
        }
        out.push(path + '  [x=' + Math.round(r.left) + ' w=' + r.width.toFixed(1) + ' h=' + Math.round(r.height) + ']');
      }
    } catch (e) { return ['scan error: ' + e.message]; }
    return out.length ? out : ['none found'];
  }

  var CC_BUILD = 'CC-BUILD-2026-07-31';

  function ccDiagnose() {
    var onProduct = /\/products\//.test(location.pathname);
    var d = {
      build: CC_BUILD,
      cssLoaded: !!getComputedStyle(document.documentElement).getPropertyValue('--cc-radius').trim(),
      onProductPage: onProduct,
      variantJson: !!document.querySelector('[data-variant-json]'),
      variantCount: ccVariants ? ccVariants.length : 0,
      slideMap: !!document.querySelector('[data-cc-variant-media]'),
      slideMapEntries: ccVariantMedia ? Object.keys(ccVariantMedia).length : 0,
      optionGroups: optionWraps().length,
      gallerySlides: (function () {
        var g = galleryItems();
        return g ? g.type + ':' + g.items.length : 0;
      })(),
      themeJsAlive: !!(window.theme && window.theme.Variants),
      strayLines: findStrayLines(),
      linesRemoved: ccKilledLines.length ? ccKilledLines : 'none',
      reducedMotionOn: !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches),
      animationsRunning: motionOK(),
      revealTagged: document.querySelectorAll('[data-cc-reveal-child]').length,
      currentVariantHasImage: (function () {
        try {
          var v = resolveCurrentVariant(currentSelections(optionWraps()));
          if (!v) return 'no variant resolved';
          if (!ccVariantMedia) return 'map missing';
          var i = ccVariantMedia[v.id];
          if (i === undefined || i === null) return 'variant ' + v.id + ' has NO image linked in Shopify';
          var g = galleryItems();
          return (g && g.items[i]) ? 'OK -> ' + g.type + ' item ' + i
                                   : 'image ' + i + ' not found in gallery';
        } catch (e) { return 'error: ' + e.message; }
      })(),
      sectionConfig: !!document.getElementById('cc-config')
    };
    try { console.log('[conversion layer]', JSON.stringify(d)); } catch (e) {}

    if (location.search.indexOf('ccdebug=1') === -1) return;

    var box = document.createElement('div');
    box.setAttribute('style',
      'position:fixed;z-index:99999;left:8px;bottom:8px;max-width:340px;' +
      'background:#111;color:#fff;font:12px/1.5 monospace;padding:12px 14px;' +
      'border-radius:8px;box-shadow:0 6px 24px rgba(0,0,0,.4);white-space:pre-wrap;');
    var lines = ['CONVERSION LAYER DIAGNOSTIC', '---------------------------'];
    var explain = {
      cssLoaded: 'stylesheet reached the page',
      variantJson: 'variant data present',
      slideMap: 'colour->image map present',
      optionGroups: 'option button groups found',
      gallerySlides: 'gallery slides found',
      themeJsAlive: "theme's own JS running",
      sectionConfig: 'Conversion booster section present'
    };
    for (var k in d) {
      var v = d[k];
      var mark = (v === true || (typeof v === 'number' && v > 0)) ? 'OK ' :
                 (v === false || v === 0) ? 'XX ' : '   ';
      lines.push(mark + k + ': ' + v + (explain[k] ? '  (' + explain[k] + ')' : ''));
    }
    box.textContent = lines.join('\n');
    document.body.appendChild(box);
  }

  /* ------------------------------------------------------------
     Main ADD TO CART: press animation + a click fallback.
     If no submit event reaches us (button outside the form, another
     script calling stopPropagation, the theme editor swallowing it),
     the add still happens. Guarded so it can never double-add.
     ------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-add-to-cart], .add-to-cart');
    if (!btn) return;
    // these have their own handlers
    if (btn.closest('.quick-add, .sticky-atc, .details-panel, .cart-upsells')) return;

    btn.classList.add('cc-atc-press');
    setTimeout(function () { btn.classList.remove('cc-atc-press'); }, 360);

    var form = btn.closest('form.product-single__form') ||
               document.querySelector('form.product-single__form');
    if (!form) return;

    setTimeout(function () {
      if (Date.now() - ccLastSubmit < 1200) return;   // submit path already ran
      if (btn.classList.contains('btn--loading')) return;
      ajaxAddForm(form, btn);
    }, 150);

    // Watchdog. theme.js's add-to-cart fetch has no .catch(), so any
    // failed request leaves the button spinning and its internal
    // loading flag stuck true — bricking every later click. If it's
    // still spinning with nothing added, clear it and do the add here.
    setTimeout(function () {
      if (!btn.classList.contains('btn--loading')) return;
      if (ccAddCompletedAt && Date.now() - ccAddCompletedAt < 4000) {
        btn.classList.remove('btn--loading');
        return;
      }
      btn.classList.remove('btn--loading');
      ajaxAddForm(form, btn);
    }, 2200);
  });

  // Clear a stuck spinner on any button, however the add was triggered.
  document.addEventListener('ajaxProduct:added', function () {
    setTimeout(function () {
      document.querySelectorAll('.btn--loading').forEach(function (el) {
        el.classList.remove('btn--loading');
      });
    }, 150);
  });

  var ccAddCompletedAt = 0;
  document.addEventListener('ajaxProduct:added', function () { ccAddCompletedAt = Date.now(); });

  // confirmation pulse once the item lands
  document.addEventListener('ajaxProduct:added', function () {
    var b = document.querySelector('.product-single__form [data-add-to-cart]');
    if (!b) return;
    b.classList.add('cc-atc-done');
    setTimeout(function () { b.classList.remove('cc-atc-done'); }, 900);
  });

  /* ------------------------------------------------------------
     Page entry animation.
     The reference does NOT animate on the way out — content is
     replaced instantly by a normal page load, then animates in on
     arrival. Intercepting clicks to fade out is exactly what causes
     a white flash mid-navigation, so we deliberately don't.
     ------------------------------------------------------------ */
  function initPageTransitions() {
    if (!motionOK()) return;
    document.body.classList.add('cc-page-ready');
  }

  /* Stagger by visual ROW so each row of products rises together and
     the next follows — the wave pattern in the reference. Rows are
     measured live, so it adapts to however many columns the grid has
     at the current screen size. */
  function applyRowStagger(els) {
    if (!els || !els.length) return;
    var rows = [];
    Array.prototype.forEach.call(els, function (el) {
      var top = el.getBoundingClientRect().top + window.pageYOffset;
      var row = null;
      for (var i = 0; i < rows.length; i++) {
        if (Math.abs(rows[i].top - top) < 60) { row = rows[i]; break; }
      }
      if (!row) { row = { top: top, items: [] }; rows.push(row); }
      row.items.push(el);
    });
    rows.sort(function (a, b) { return a.top - b.top; });
    rows.forEach(function (row, i) {
      // ~0.35s between rows, measured from the reference: each row
      // finishes fading before the next starts, giving distinct waves
      // rather than one blurred cascade. Capped so long pages don't
      // leave the last rows waiting.
      var delay = Math.min(i * 0.34, 1.4);
      row.items.forEach(function (el) { el.style.transitionDelay = delay + 's'; });
    });
  }

  /* ------------------------------------------------------------
     Scroll reveal — mobile and desktop.
     The theme's AOS only runs if its JS initialises, which is
     unreliable here, so this is self-contained. Elements rise into
     place as they enter the viewport. Safety net: if the observer
     never fires (old browser, JS error), everything is revealed.
     ------------------------------------------------------------ */
  function initScrollReveal(root) {
    if (!motionOK()) {
      document.querySelectorAll('[data-cc-reveal], [data-cc-reveal-child]')
        .forEach(function (el) { el.classList.add('cc-in'); });
      return;
    }

    // Build the observer BEFORE hiding anything. Checking that
    // IntersectionObserver merely exists isn't enough — on some older
    // devices it exists but throws on construction, which would hide
    // the page and never reveal it.
    var probe = null;
    try {
      probe = new IntersectionObserver(function () {});
      probe.disconnect();
    } catch (e) {
      probe = null;
    }
    if (!probe) {
      document.querySelectorAll('[data-cc-reveal], [data-cc-reveal-child]')
        .forEach(function (el) { el.classList.add('cc-in'); });
      return;
    }
    document.documentElement.classList.add('cc-reveal-ready');

    var scope = root || document;

    // tag the things worth revealing, once
    var targets = [
      '.grid-product',
      '.trust-bar',
      '.index-section > .page-width',
      '.collection-grid__wrapper',
      '.product-block--sales-point',
      '.rte--block'
    ].join(',');

    scope.querySelectorAll(targets).forEach(function (el) {
      if (el.hasAttribute('data-cc-reveal') || el.hasAttribute('data-cc-reveal-child')) return;
      if (el.closest('#CartDrawer, .details-panel, .sticky-atc')) return;
      // Never animate a container that holds a full-screen popup: the
      // transform would make it the positioning root for that popup.
      if (el.querySelector('.stl-modal, .details-panel, .drawer')) return;
      el.setAttribute('data-cc-reveal-child', '');
    });

    var items = scope.querySelectorAll('[data-cc-reveal]:not(.cc-in), [data-cc-reveal-child]:not(.cc-in)');
    if (!items.length) return;

    if (!('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('cc-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // reveal on entry, and also if it was scrolled past (a fast jump
        // can skip the intersecting state entirely, which would leave the
        // content permanently invisible)
        if (!entry.isIntersecting && entry.boundingClientRect.top > 0) return;
        entry.target.style.transitionDelay = '';
        entry.target.classList.add('cc-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });

    var onscreen = [];
    Array.prototype.forEach.call(items, function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) {
        onscreen.push(el);
      } else {
        io.observe(el);
      }
    });

    // everything visible at load rises in row-by-row waves
    applyRowStagger(onscreen);
    if (onscreen.length) onscreen[0].offsetHeight;   // flush styles first,
    requestAnimationFrame(function () {              // else it jumps with no fade
      requestAnimationFrame(function () {
        onscreen.forEach(function (el) { el.classList.add('cc-in'); });
      });
    });

    // failsafe: nothing stays invisible
    setTimeout(function () {
      scope.querySelectorAll('[data-cc-reveal]:not(.cc-in), [data-cc-reveal-child]:not(.cc-in)')
        .forEach(function (el) {
          var r = el.getBoundingClientRect();
          if (r.top < window.innerHeight * 1.5) el.classList.add('cc-in');
        });
    }, 2500);
  }

  /* ------------------------------------------------------------
     Conversion triggers: keep the savings figure, stock meter and
     delivery estimate accurate as the shopper switches variant.
     Every value is derived from real store data — if a variant has
     no compare-at price or no tracked stock, the element hides
     rather than showing something invented.
     ------------------------------------------------------------ */
  function updateTriggers(variant) {
    // savings anchor
    var sv = document.querySelector('[data-ct-savings]');
    if (sv && variant) {
      var cmp = variant.compare_at_price || 0;
      if (cmp > variant.price) {
        var saved = cmp - variant.price;
        var pct = Math.round((saved * 100) / cmp);
        var badge = sv.querySelector('.ct-savings__badge');
        var amt = sv.querySelector('[data-ct-saved]');
        if (badge) badge.textContent = '\u2212' + pct + '%';
        if (amt) amt.textContent = money(saved);
        sv.style.display = '';
      } else {
        sv.style.display = 'none';
      }
    }

    // stock meter — real inventory only
    var st = document.querySelector('[data-ct-stock]');
    if (st && variant) {
      var qty = variant.inventory_quantity;
      var mgmt = variant.inventory_management;
      var policy = variant.inventory_policy;
      var thr = parseInt(st.dataset.threshold, 10) || 10;
      var show = mgmt === 'shopify' && policy !== 'continue' &&
                 typeof qty === 'number' && qty > 0 && qty <= thr;
      st.style.display = show ? '' : 'none';
      if (show) {
        var fill = st.querySelector('.ct-stock__fill');
        if (fill) fill.style.width = Math.max(6, Math.round((qty * 100) / thr)) + '%';
        var strong = st.querySelector('.ct-stock__label strong');
        if (strong) strong.textContent = qty;
      }
    }
  }

  /* delivery estimate + order-by countdown, from your configured
     cutoff and transit times. Weekends skipped. */
  function initDelivery() {
    var el = document.querySelector('[data-ct-delivery]');
    if (!el) return;
    if (el.dataset.ccDisabled === '1') { el.remove(); return; }
    var cutoff = parseInt(el.dataset.cutoffHour, 10);
    if (isNaN(cutoff)) cutoff = 14;
    var minD = parseInt(el.dataset.minDays, 10) || 2;
    var maxD = parseInt(el.dataset.maxDays, 10) || 5;

    function addBusinessDays(from, n) {
      var d = new Date(from.getTime());
      var added = 0;
      while (added < n) {
        d.setDate(d.getDate() + 1);
        var day = d.getDay();
        if (day !== 0 && day !== 6) added++;
      }
      return d;
    }

    function fmt(d) {
      return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    }

    function tick() {
      var now = new Date();
      var beforeCutoff = now.getHours() < cutoff;
      var isWeekday = now.getDay() !== 0 && now.getDay() !== 6;
      var base = new Date(now.getTime());
      if (!(beforeCutoff && isWeekday)) base = addBusinessDays(base, 1);

      var a = addBusinessDays(base, minD);
      var bDate = addBusinessDays(base, maxD);
      var range = fmt(a) + ' \u2013 ' + fmt(bDate);
      var arrivesEl = el.querySelector('[data-ct-arrives]');
      if (arrivesEl) {
        arrivesEl.textContent = (el.dataset.arrives || 'Estimated delivery [range]')
          .replace('[range]', range);
      }

      var urg = el.querySelector('[data-ct-urgency]');
      var urgText = el.querySelector('[data-ct-urgency-text]');
      if (urg && urgText) {
        if (beforeCutoff && isWeekday) {
          var end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), cutoff, 0, 0);
          var ms = end - now;
          var h = Math.floor(ms / 3600000);
          var m = Math.floor((ms % 3600000) / 60000);
          var s = Math.floor((ms % 60000) / 1000);
          var t = (h > 0 ? h + 'h ' : '') + m + 'm ' + (h > 0 ? '' : s + 's');
          urgText.textContent = (el.dataset.orderWithin || 'Order within [time] for dispatch today')
            .replace('[time]', t.trim());
          urg.removeAttribute('hidden');
        } else {
          urg.setAttribute('hidden', 'hidden');
        }
      }
    }

    tick();
    setInterval(tick, 1000);
  }

  /* ============================================================
     CONVERSION TRIGGERS
     All driven by real data:
       - savings anchor  <- actual compare_at_price
       - stock bar       <- actual inventory_quantity
       - delivery date   <- real cutoff + business-day transit
     Nothing invented, so nothing contradicts itself on refresh.
     ============================================================ */
  function ccMoneyShort(cents) {
    var v = (cents / 100).toFixed(2);
    if (v.slice(-3) === '.00') v = v.slice(0, -3);
    return SYMBOL + v;
  }

  function updateSavings(v) {
    var el = document.querySelector('[data-cc-savings]');
    if (!el) return;
    var was = v && v.compare_at_price;
    if (!was || was <= v.price) { el.hidden = true; return; }
    var saved = was - v.price;
    var pct = Math.round((saved / was) * 100);
    el.innerHTML = '<span class="cc-savings__pill">Save ' + pct + '%</span>' +
                   '<span class="cc-savings__was">' + ccMoneyShort(was) + '</span>' +
                   '<span class="cc-savings__amt">You save ' + ccMoneyShort(saved) + '</span>';
    el.hidden = false;
  }

  function updateStockBar(v) {
    var el = document.querySelector('[data-cc-stockbar]');
    if (!el) return;
    var threshold = parseInt(el.dataset.threshold, 10) || 15;
    var qty = v && typeof v.inventory_quantity === 'number' ? v.inventory_quantity : null;
    var tracked = v && v.inventory_management;

    // Only ever shown when Shopify is actually tracking stock and the
    // real number is at or below the threshold.
    if (!tracked || qty === null || qty <= 0 || qty > threshold) {
      el.hidden = true;
      el.classList.remove('is-critical');
      return;
    }
    var pct = Math.max(6, Math.round((qty / threshold) * 100));
    var fill = el.querySelector('.cc-stockbar__fill');
    var label = el.querySelector('.cc-stockbar__label');
    if (fill) fill.style.width = pct + '%';
    if (label) label.textContent = qty === 1 ? 'Only 1 left in this size'
                                             : 'Only ' + qty + ' left in this size';
    el.classList.toggle('is-critical', qty <= 3);
    el.hidden = false;
  }

  var CC_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var CC_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function addBusinessDays(from, days) {
    var d = new Date(from.getTime());
    var added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      var wd = d.getDay();
      if (wd !== 0 && wd !== 6) added++;
    }
    return d;
  }

  function updateDelivery() {
    var el = document.querySelector('[data-cc-delivery]');
    if (el && el.dataset.ccDisabled === '1') { el.remove(); return; }
    if (!el) return;
    var cutoff = parseInt(el.dataset.cutoff, 10);
    if (isNaN(cutoff)) cutoff = 14;
    var minD = parseInt(el.dataset.min, 10) || 2;
    var maxD = parseInt(el.dataset.max, 10) || 5;

    var now = new Date();
    var wd = now.getDay();
    var isWeekend = (wd === 0 || wd === 6);
    var beforeCutoff = !isWeekend && now.getHours() < cutoff;

    // ships today if before cutoff on a business day, else next business day
    var ship = new Date(now.getTime());
    if (!beforeCutoff) ship = addBusinessDays(now, 1);

    var lo = addBusinessDays(ship, minD);
    var hi = addBusinessDays(ship, maxD);
    var fmt = function (d) { return CC_DAYS[d.getDay()] + ' ' + d.getDate() + ' ' + CC_MONTHS[d.getMonth()]; };
    var range = (fmt(lo) === fmt(hi)) ? fmt(lo) : fmt(lo) + ' \u2013 ' + fmt(hi);

    if (beforeCutoff) {
      var end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), cutoff, 0, 0);
      var ms = end - now;
      var h = Math.floor(ms / 3600000);
      var m = Math.floor((ms % 3600000) / 60000);
      var left = (h > 0 ? h + 'h ' : '') + m + 'm';
      el.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">' +
        '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg><span>' +
        escapeHtml(el.dataset.orderText || '')
          .replace('[time]', '<strong>' + left + '</strong>')
          .replace('[date]', '<strong>' + range + '</strong>') + '</span>';
    } else {
      el.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">' +
        '<path d="M3 7h13v10H3zM16 10h3.5l1.5 3v4h-5"/><circle cx="7" cy="18" r="1.6"/><circle cx="18" cy="18" r="1.6"/></svg><span>' +
        escapeHtml(el.dataset.plainText || '').replace('[date]', '<strong>' + range + '</strong>') + '</span>';
    }
    el.hidden = false;
  }

  function refreshTriggers(variant) {
    var v = variant || (ccVariants ? resolveCurrentVariant(currentSelections(optionWraps())) : null);
    if (v) { updateSavings(v); updateStockBar(v); }
    updateDelivery();
  }

  function initTriggers() {
    if (!document.querySelector('.cc-triggers')) return;
    refreshTriggers();
    document.addEventListener('variant:change', function (e) {
      refreshTriggers(e && e.detail && e.detail.variant);
    });
    setInterval(updateDelivery, 60000); // keep the countdown honest
  }

  /* --- consistency: genuinely recently viewed products ---------
     Records handles the shopper actually opened, then renders them
     back on the cart sheet and product pages. Real behaviour, no
     invented "others also bought" claims.
     ------------------------------------------------------------ */
  var CC_RECENT_KEY = 'cc_recent_products';

  function recentList() {
    try { return JSON.parse(localStorage.getItem(CC_RECENT_KEY) || '[]'); }
    catch (e) { return []; }
  }

  function trackRecentlyViewed() {
    var m = location.pathname.match(/\/products\/([^\/?#]+)/);
    if (!m) return;
    try {
      var list = recentList().filter(function (h) { return h !== m[1]; });
      list.unshift(m[1]);
      localStorage.setItem(CC_RECENT_KEY, JSON.stringify(list.slice(0, 10)));
    } catch (e) {}
  }

  function renderRecentlyViewed() {
    var host = document.querySelector('[data-cc-recent]');
    if (!host) return;
    var current = (location.pathname.match(/\/products\/([^\/?#]+)/) || [])[1];
    var list = recentList().filter(function (h) { return h !== current; }).slice(0, 4);
    if (!list.length) return;

    var home = homeUrl();
    var done = 0, cards = [];
    list.forEach(function (handle, i) {
      fetchJson(home + 'products/' + handle + '.js').then(function (p) {
        if (p && p.title) {
          cards[i] = {
            title: p.title,
            url: home + 'products/' + handle,
            price: p.price,
            img: (p.featured_image || (p.images && p.images[0]) || '')
          };
        }
        if (++done === list.length) {
          var html = cards.filter(Boolean).map(function (c) {
            return '<a class="cc-recent__card" href="' + escapeHtml(c.url) + '">' +
              (c.img ? '<img src="' + escapeHtml(c.img) + '" alt="' + escapeHtml(c.title) + '" loading="lazy">' : '<span class="cc-recent__blank"></span>') +
              '<span class="cc-recent__name">' + escapeHtml(c.title) + '</span>' +
              '<span class="cc-recent__price">' + money(c.price) + '</span></a>';
          }).join('');
          if (html) {
            host.querySelector('.cc-recent__strip').innerHTML = html;
            host.removeAttribute('hidden');
          }
        }
      });
    });
  }

  /* ------------------------------------------------------------
     Low-stock line: keep it in step with the selected variant.
     The theme only refreshes this from its own variant system, which
     doesn't run reliably here, so the count stayed on whatever variant
     the page loaded with. Quantities come from [data-cc-inventory]
     because Shopify's variant JSON omits inventory_quantity.
     ------------------------------------------------------------ */
  var ccInventory = null;

  function readInventoryMap() {
    if (ccInventory) return ccInventory;
    var el = document.querySelector('[data-cc-inventory]');
    if (!el) return null;
    try { ccInventory = JSON.parse(el.textContent); } catch (e) { ccInventory = null; }
    return ccInventory;
  }

  function updateInventory(variant) {
    if (!variant) return;
    var target = document.querySelector('[data-product-inventory]');
    if (!target) return;
    var wrap = (target.closest && target.closest('.icon-and-text')) || target.parentNode;
    var strings = (window.theme && window.theme.strings) || {};

    if (variant.available === false) {
      if (wrap && wrap.classList) wrap.classList.add('hide');
      return;
    }
    if (wrap && wrap.classList) wrap.classList.remove('hide');

    var threshold = parseInt(target.dataset.threshold || '0', 10);
    var qty = inventoryQtyFor(variant.id);

    var text = '';
    if (qty != null && qty > 0 && threshold > 0 && qty <= threshold) {
      var tpl = (qty === 1)
        ? (strings.stockLabelOne || 'Low stock - [count] item left')
        : (strings.stockLabelOther || 'Low stock - [count] items left');
      text = String(tpl).replace('[count]', qty).replace('{{ count }}', qty);
      if (wrap && wrap.classList) wrap.classList.add('inventory--low');
    } else {
      // The theme's own in-stock string can come through blank, which
      // left a bare dot with no words. Always fall back to real text.
      text = (strings.inStockLabel || '').trim() || 'In stock, ready to ship';
      if (wrap && wrap.classList) wrap.classList.remove('inventory--low');
    }
    target.textContent = text;
    fillEmptyStock();
  }

  /* Quantities: prefer the theme's own window.inventories (always
     emitted by product-inventory.liquid), then our own map. */
  function inventoryQtyFor(variantId) {
    try {
      var inv = window.inventories;
      if (inv) {
        for (var pid in inv) {
          if (inv[pid] && inv[pid][variantId] &&
              typeof inv[pid][variantId].quantity === 'number') {
            if (inv[pid][variantId].policy === 'continue') return null;
            return inv[pid][variantId].quantity;
          }
        }
      }
    } catch (e) {}
    var map = readInventoryMap();
    return (map && map[variantId] != null) ? map[variantId] : null;
  }

  /* Never leave the dot standing on its own — give it words. */
  function fillEmptyStock() {
    var strings = (window.theme && window.theme.strings) || {};
    document.querySelectorAll('[data-product-inventory]').forEach(function (t) {
      var row = (t.closest && (t.closest('.sales-point') || t.closest('.icon-and-text'))) || t.parentNode;
      if (!t.textContent.trim()) {
        t.textContent = (strings.inStockLabel || '').trim() || 'In stock, ready to ship';
      }
      if (row && row.classList) row.classList.remove('cc-stock-empty');
    });
  }

  /* Promo code widget — used in the slide-up cart AND on the cart page. */
  function bindPromo(promo) {
    if (!promo || promo.dataset.ccPromoBound) return;
    promo.dataset.ccPromoBound = '1';

    var toggle = promo.querySelector('.cart-promo__toggle');
    var input = promo.querySelector('.cart-promo__input');
    var apply = promo.querySelector('.cart-promo__apply');
    var chipCode = promo.querySelector('[data-promo-code]');
    var remove = promo.querySelector('.cart-promo__remove');

    if (toggle) toggle.addEventListener('click', function () {
      promo.classList.add('is-open');
      if (input) input.focus();
    });
    function applyCode() {
      var code = ((input && input.value) || '').trim();
      if (!code) return;
      promoCode = code;
      if (chipCode) chipCode.textContent = code.toUpperCase();
      promo.classList.add('has-code');
    }
    if (apply) apply.addEventListener('click', applyCode);
    if (input) input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); applyCode(); }
    });
    if (remove) remove.addEventListener('click', function () {
      promoCode = '';
      promo.classList.remove('has-code', 'is-open');
      if (input) input.value = '';
    });
  }

  /* Cart PAGE: bind the promo field and carry the code into checkout. */
  function initCartPagePromo() {
    var promo = document.querySelector('.cart-promo--page');
    if (!promo) return;
    bindPromo(promo);

    var form = promo.closest('form') ||
               document.querySelector('form[action*="/cart"]');
    if (!form || form.dataset.ccPromoSubmit) return;
    form.dataset.ccPromoSubmit = '1';
    form.addEventListener('submit', function (e) {
      if (!promoCode) return;
      var terms = form.querySelector('.cart__terms-checkbox');
      if (terms && !terms.checked) return;
      e.preventDefault();
      e.stopPropagation();
      window.location.href = '/discount/' + encodeURIComponent(promoCode) + '?redirect=%2Fcheckout';
    }, true);
  }

  /* ------------------------------------------------------------
     PORTAL OVERLAYS TO <body>
     position:fixed is measured against the nearest ancestor that has
     a transform, filter, perspective or will-change — not always the
     viewport. Any such ancestor (a page animation, a scroll-reveal
     mid-flight, a theme section, an app wrapper) silently turns a
     full-screen popup into a box tucked inside that element, which is
     why the cart sheet, See details and Shop the look failed on some
     devices and not others. Re-parenting them to <body> removes every
     one of those ancestors permanently.
     ------------------------------------------------------------ */
  function portalOverlays() {
    if (!document.body) return;
    var sel = [
      '#CartDrawer',
      '.details-panel',
      '.details-backdrop',
      '.sticky-atc'
      // NOT .stl-modal — Shop the look styles it as
      // "#ShopTheLook-<id> .stl-modal", so moving it out of that
      // wrapper strips every rule it needs, including position:fixed
      // and its open state. It's handled by the transform fix instead.
    ];
    for (var i = 0; i < sel.length; i++) {
      var nodes = document.querySelectorAll(sel[i]);
      for (var j = 0; j < nodes.length; j++) {
        var el = nodes[j];
        if (el.parentNode === document.body) continue;
        if (el.getAttribute('data-cc-portaled')) continue;
        el.setAttribute('data-cc-portaled', '1');
        document.body.appendChild(el);
      }
    }
  }

  /* ------------------------------------------------------------
     FAVICON
     The Conversion booster section renders in <body>, so its favicon
     links are relocated into <head> and any existing icon links are
     dropped first — browsers otherwise keep showing the old one.
     ------------------------------------------------------------ */
  function applyFavicon() {
    var mine = document.querySelectorAll('[data-cc-favicon]');
    if (!mine.length) return;
    var head = document.head || document.getElementsByTagName('head')[0];
    if (!head) return;

    // remove icons that aren't ours so the browser can't prefer them
    var existing = document.querySelectorAll('link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]');
    for (var i = 0; i < existing.length; i++) {
      if (!existing[i].hasAttribute('data-cc-favicon')) existing[i].parentNode.removeChild(existing[i]);
    }
    for (var j = 0; j < mine.length; j++) {
      if (mine[j].parentNode !== head) head.appendChild(mine[j]);
    }
  }

  function initAll(root) {
    initQuickAdd(root);
    applyConfigDrops(root);
    initDrops(root);
  }

  document.addEventListener('DOMContentLoaded', function () {
    safe('applyFavicon', applyFavicon);
    safe('portalOverlays', portalOverlays);
    safe('readConfig', function () { readConfig(); });
    safe('initAll', function () { initAll(document); });
    safe('applyConfigTexts', function () { applyConfigTexts(); });
    safe('fixVariantLabels', function () { fixVariantLabels(); });
    document.addEventListener('variant:change', fixVariantLabels);
    document.addEventListener('variant:change', function (e) {
      if (e && e.detail && e.detail.variant) updateInventory(e.detail.variant);
    });
    safe('initProductOptions', function () { initProductOptions(); });
    safe('fillEmptyStock', function () { fillEmptyStock(); });
    safe('initDropNotify', function () { initDropNotify(); });
    safe('initCartPagePromo', function () { initCartPagePromo(); });
    safe('initPageTransitions', function () { initPageTransitions(); });
    safe('initTriggers', function () { initTriggers(); });
    safe('trackRecentlyViewed', function () { trackRecentlyViewed(); });
    safe('renderRecentlyViewed', function () { renderRecentlyViewed(); });
    safe('initScrollReveal', function () { initScrollReveal(document); });
    // Whatever happens above, nothing is left hidden.
    setTimeout(function () {
      safe('revealFailsafe', function () {
        document.querySelectorAll('[data-cc-reveal]:not(.cc-in), [data-cc-reveal-child]:not(.cc-in)')
          .forEach(function (el) { el.classList.add('cc-in'); });
      });
    }, 3000);
    safe('killStrayLines', function () { killStrayLines(); });
    setTimeout(function () { safe('killStrayLines', killStrayLines); }, 700);
    setTimeout(function () { safe('killStrayLines', killStrayLines); }, 2000);
    safe('initDelivery', function () { initDelivery(); });
    setTimeout(function () { safe('portalOverlays', portalOverlays); }, 900);
    (function () {
      var v0 = resolveCurrentVariant(currentSelections(optionWraps()));
      if (v0) updateTriggers(v0);
    })();
    document.addEventListener('variant:change', function (e) {
      if (e && e.detail && e.detail.variant) updateTriggers(e.detail.variant);
    });
    var mainEl = document.getElementById('MainContent');
    if (mainEl && window.MutationObserver) {
      var revealObs = new MutationObserver(function () { initScrollReveal(document); });
      revealObs.observe(mainEl, { childList: true, subtree: true });
    }
    ccDiagnose();
    if (!(CFG && CFG.enabled && CFG.enabled.stickyAtc === false)) initStickyBar();
    watchCartRebuilds();
    initSheetControls();
    tick();

    var main = document.getElementById('MainContent');
    if (main) {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          if (muts[i].addedNodes.length) { initAll(document); break; }
        }
      }).observe(main, { childList: true, subtree: true });
    }
  });
})();
